// What to write when a record changed, as the kit's FieldChanges.swift and the write path of FirestoreBackend.swift
// (`apply`) do it (docs/47, rules 2 and 3):
//
// - A record new here is written whole, with `modified` set by the server.
// - A changed record writes only its top-level fields that differ from what was known before, each replaced whole
//   (merged by field, never into a field, so a map such as `start` can change its case). A field that became absent
//   is deleted. `deleted` is always deleted (an edit wins over a deletion from elsewhere that hasn't arrived yet) and
//   `modified` set by the server. Fields this version doesn't know are never touched: a newer version may have added
//   them, and two devices may change different fields of the same record without one undoing the other.
// - A deleted record leaves a tombstone, `{deleted: true, modified}`, so other devices learn of it through the same
//   question as of any change.
// - A stored record of the person's own that can't be read only because fields it needs are missing (an edit that
//   landed on a tombstone) gets them from this device's copy, and nothing else (`missingFieldsWrite`).
//
// The core knows no Firestore: "delete this field" and "the server's time" are the sentinels below, which the data
// layer maps to `deleteField()` and `serverTimestamp()`.

import { codecs, type CollectionName, type RecordTypes } from "./records";
import type { Placement } from "./records/share";
import { encodePlacement } from "./records/share";
import type { Todo } from "./records/todo";
import { sameValue, storedFields, type EncodeOptions, type Fields } from "./records/values";

/** "Delete this field": the data layer writes `deleteField()`. */
export const DELETE_FIELD: unique symbol = Symbol("deleteField");
/** "The server's time": the data layer writes `serverTimestamp()`. */
export const SERVER_TIMESTAMP: unique symbol = Symbol("serverTimestamp");

export type Sentinel = typeof DELETE_FIELD | typeof SERVER_TIMESTAMP;

/** One write to one document. */
export type Write =
  /** Replaces the whole document (`setDoc(ref, fields)`). */
  | { kind: "set"; fields: Fields }
  /** Writes these fields and leaves the others (`setDoc(ref, fields, { mergeFields: Object.keys(fields) })`). */
  | { kind: "merge"; fields: Fields };

/** The fields that differ between two encoded records, and those the old one had and the new one doesn't. */
export interface FieldChanges {
  /** Fields whose value is new or different, with the new value. A nested value (a map, a list) is written whole. */
  changed: Fields;
  /** Fields the old record had and the new one doesn't: an optional that became absent. Sorted. */
  removed: string[];
}

/** Compares two records as their encoders write them (FieldChanges.swift). */
export function fieldChanges(old: Fields, next: Fields): FieldChanges {
  const changed: Fields = {};
  for (const [key, value] of Object.entries(next)) {
    if (!(key in old) || !sameValue(old[key], value)) changed[key] = value;
  }
  const removed = Object.keys(old).filter((key) => !(key in next)).sort();
  return { changed, removed };
}

export function isEmpty(changes: FieldChanges): boolean {
  return Object.keys(changes.changed).length === 0 && changes.removed.length === 0;
}

export interface SaveOptions extends EncodeOptions {
  /**
   * Fields neither compared nor written: a member's own values in a share of someone else, which go to their
   * placement instead (`PERSONAL_TODO_FIELDS` and the like).
   */
  leaveOut?: readonly string[];
}

/**
 * The write for an encoded record that was saved: `before` is the record as the store knew it before the change
 * (absent for a record new here, or back from a tombstone). Null when nothing changed.
 */
export function writeForSave(before: Fields | undefined, after: Fields, options?: SaveOptions): Write | null {
  const leaveOut = options?.leaveOut ?? [];
  const without = (fields: Fields): Fields =>
    leaveOut.length === 0 ? fields : Object.fromEntries(Object.entries(fields).filter(([key]) => !leaveOut.includes(key)));
  const next = without(after);
  if (before === undefined) return { kind: "set", fields: { ...storedFields(next, options), modified: SERVER_TIMESTAMP } };
  const changes = fieldChanges(without(before), next);
  if (isEmpty(changes)) return null;
  const fields: Fields = storedFields(changes.changed, options);
  for (const key of changes.removed) fields[key] = DELETE_FIELD;
  fields.deleted = DELETE_FIELD;
  fields.modified = SERVER_TIMESTAMP;
  return { kind: "merge", fields };
}

/** The write for a deleted record: a tombstone in place of the document. */
export function tombstoneWrite(): Write {
  return { kind: "set", fields: { deleted: true, modified: SERVER_TIMESTAMP } };
}

/** The write for a record of a collection: `before` absent for a new one, `after` absent for a deletion. */
export function recordWrite<Name extends CollectionName>(
  collection: Name, before: RecordTypes[Name] | undefined, after: RecordTypes[Name] | undefined, options?: SaveOptions,
): Write | null {
  if (after === undefined) return tombstoneWrite();
  const codec = codecs[collection];
  // Both sides are compared with their moments as Dates; only what is written becomes stored moments.
  return writeForSave(before === undefined ? undefined : codec.encode(before), codec.encode(after), options);
}

/**
 * The write for a person's placement in a share (FirestoreBackend.setPlacement): the fields it has, merged by field,
 * with `modified`. A field that became absent is not written, and stays as it was on the server, as in the kit.
 */
export function placementWrite(placement: Placement, options?: EncodeOptions): Write {
  return { kind: "merge", fields: { ...encodePlacement(placement, options), modified: SERVER_TIMESTAMP } };
}

/**
 * The write that makes a stored record readable again from this device's own copy, or null (FieldChanges.swift:
 * `FieldChanges.missing`). An edit of only the changed fields that lands on a tombstone, or on no document, leaves a
 * record without its title, order and the like (2026-10-27). Only the fields it lacks and can't be read without are
 * written: one it lacks that is optional may have been removed on purpose, and one it has may be newer than this
 * copy. A record that still can't be read with them (a shape of a newer version) is left as it is.
 */
export function missingFieldsWrite(stored: Fields, own: Fields, readable: (fields: Fields) => boolean, options?: EncodeOptions): Write | null {
  const lacking = Object.keys(own).filter((key) => !(key in stored) || stored[key] === null);
  const needed = lacking.filter((key) => {
    const without = { ...own };
    delete without[key];
    return !readable(without);
  });
  if (needed.length === 0) return null;
  const fields = storedFields(Object.fromEntries(needed.map((key) => [key, own[key]])), options);
  if (!readable({ ...stored, ...fields })) return null;
  return { kind: "merge", fields: { ...fields, modified: SERVER_TIMESTAMP } };
}

// MARK: - The editor's draft

/** The fields an editor's draft takes over when the user changed them (Models.swift: Todo.merging), in that order. */
const MERGED_TODO_FIELDS = [
  "title", "notes", "start", "deadline", "reminder", "place", "repeats", "calendarEventID", "tags", "checklist",
  "projectID", "sectionID", "todaySectionID", "areaID", "order", "createdAt", "leftInboxAt", "seriesReminder",
  "isSample", "reminderZone", "seriesReminderZone", "plannedUntil", "planOrder", "spawnedFrom", "keptAt", "habitID",
] as const satisfies readonly (keyof Todo)[];

/**
 * The draft's changes, measured against `base` (the to-do as it was when editing began), on top of `stored` (the
 * to-do as it is now): a field the user changed takes the draft's value, every other one keeps what is stored, so a
 * change from elsewhere that arrived meanwhile survives. A field both changed goes to the user. Completion travels
 * with who did it.
 */
export function mergeTodoDraft(draft: Todo, base: Todo, stored: Todo): Todo {
  const result: Todo = { ...stored };
  const take = (key: keyof Todo) => {
    if (sameValue(draft[key], base[key])) return;
    if (draft[key] === undefined) delete result[key];
    else (result as unknown as Record<string, unknown>)[key] = draft[key];
  };
  for (const key of MERGED_TODO_FIELDS) take(key);
  if (!sameValue(draft.completedAt, base.completedAt)) {
    take("completedAt");
    if (draft.completedBy === undefined) delete result.completedBy;
    else result.completedBy = draft.completedBy;
  }
  return result;
}
